import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  QueryList,
  ViewChildren,
  SimpleChanges,
  OnChanges,
  TemplateRef,
  ViewChild,
  OnDestroy,
} from '@angular/core';
import { Observable, Subject } from 'rxjs';
import {
  DRAG_SELECTION_HOST,
  type DragSelectionDelegate,
  type DragSelectionHitTester,
  type DragSelectionHost,
} from '../../directives/drag-selection';
import { VirtualScrollDelegate } from './virtual-scroll.delegate';

export interface VirtualScrollOffset {
  scrollTop: number;
  scrollLeft: number;
}

@Component({
    selector: 'ts-virtual-scroll',
    templateUrl: './virtual-scroll.component.html',
    styleUrls: ['./virtual-scroll.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
    providers: [
    {
      provide: DRAG_SELECTION_HOST,
      useExisting: forwardRef(() => VirtualScrollComponent),
    },
  ],
})
export class VirtualScrollComponent implements OnChanges, AfterViewInit, OnDestroy, DragSelectionHost {
  @Input() public rowTemplate: TemplateRef<unknown>;
  @Input() public itemCount = 0;
  @Input() public scrollThreshold = 0;
  @Input() public paddingNodes = 1;
  @Input() public rowHeight = 0;
  @Input() public locationRow = -1;
  @Input() public gap = 0;
  @Input() public delegate: VirtualScrollDelegate;

  @Input() public dragSelectionEnabled = false;
  @Input() public dragSelectionHitTester: DragSelectionHitTester<unknown> | null = null;
  @Input() public dragSelectionDelegate: DragSelectionDelegate<unknown> | null = null;

  @Output() public startIndexChanged = new EventEmitter<number>();

  @ViewChild('viewport') public viewportElement: ElementRef<HTMLElement>;

  @ViewChild('content') public contentElement: ElementRef<HTMLElement>;

  @ViewChildren('rowElement') public rowElements!: QueryList<ElementRef>;

  public startNode = 0;
  public visibleNodesCount = 0;
  public offsetY = 0;
  public totalContentHeight = 0;
  public visibleItems = [];
  public rowHeights: number[] = []; // Store heights of individual rows
  public cumulativeHeights: number[] = []; // Store cumulative heights for binary search

  private resizeRafId: number | null = null;

  private readonly scrollSubject = new Subject<VirtualScrollOffset>();
  public readonly scroll$: Observable<VirtualScrollOffset> = this.scrollSubject.asObservable();

  private readonly resizeObserver = new ResizeObserver(() => {
    if (this.resizeRafId !== null) return;
    this.resizeRafId = requestAnimationFrame(() => {
      this.resizeRafId = null;
      this.updateVisibleNodes();
    });
  });

  constructor(private readonly changeDetector: ChangeDetectorRef, private readonly elementRef: ElementRef) {}

  public ngOnChanges(changes: SimpleChanges) {
    let shouldScrollToLocation = false;
    let shouldUpdateVisibleNodes = false;
    let shouldAdjustItemCount = false;
    let shouldInitlizeHeights = false;

    for (const input in changes) {
      const { previousValue, currentValue } = changes[input];

      if (currentValue === previousValue) continue;

      switch (true) {
        case ['itemCount', 'rowHeight'].includes(input):
          shouldUpdateVisibleNodes = shouldAdjustItemCount = shouldInitlizeHeights = true;
          break;
        case input === 'locationRow':
          shouldScrollToLocation = true;
          break;
        default:
          break;
      }
    }

    if (shouldInitlizeHeights) this.initializeHeights();
    if (shouldAdjustItemCount) this.adjustRowHeightsToItemCount();
    if (shouldUpdateVisibleNodes) this.updateVisibleNodes();
    if (shouldScrollToLocation) this.goToRow();
  }

  public ngAfterViewInit() {
    this.resizeObserver.observe(this.elementRef.nativeElement);
  }

  public ngOnDestroy(): void {
    if (this.resizeRafId !== null) cancelAnimationFrame(this.resizeRafId);
    this.resizeObserver.disconnect();
    this.scrollSubject.complete();
  }

  public trackByIndex(index: number, item: number): number {
    return item;
  }

  private goToRow() {
    const scrollHeight = this.cumulativeHeights[this.locationRow] ?? 0;
    this.viewportElement?.nativeElement?.scrollTo(0, scrollHeight);
  }

  public scrollRowIntoViewIfNeeded(row: number): void {
    if (row < 0 || row >= this.cumulativeHeights.length) return;
    const viewport = this.viewportElement?.nativeElement;
    if (!viewport) return;

    const viewportHeight = viewport.clientHeight;
    const currentScrollTop = viewport.scrollTop;

    const rowTop = row === 0 ? 0 : this.cumulativeHeights[row - 1] ?? 0;
    const rowBottom = this.cumulativeHeights[row] ?? 0;

    const fullyVisible = rowTop >= currentScrollTop && rowBottom <= currentScrollTop + viewportHeight;
    if (!fullyVisible) {
      const isBelowViewport = rowBottom > currentScrollTop + viewportHeight;
      const nextScrollTop = isBelowViewport ? rowBottom - viewportHeight : rowTop;
      viewport.scrollTo(0, nextScrollTop);
    }

    this.checkScrollThreshold();
    this.checkRowProximityToEnd(row);
  }

  public onScroll(event: Event) {
    const { scrollTop, scrollLeft } = event.target as HTMLElement;

    this.updateStartNode(scrollTop);
    this.updateVerticalOffset();
    this.updateVisibleNodes();

    this.scrollSubject.next({ scrollTop, scrollLeft });

    this.checkScrollThreshold();
  }

  public getRowAtContentY(contentY: number): number {
    return this.calculateStartNode(contentY);
  }

  public getViewport(): HTMLElement | null {
    return this.viewportElement?.nativeElement ?? null;
  }

  public getContent(): HTMLElement | null {
    return this.contentElement?.nativeElement ?? null;
  }

  public getScrollOffset$(): Observable<VirtualScrollOffset> {
    return this.scroll$;
  }

  private checkScrollThreshold(): void {
    if (this.scrollThreshold <= 0) return;
    const viewport = this.viewportElement?.nativeElement;
    if (!viewport) return;
    const { scrollTop, clientHeight, scrollHeight } = viewport;
    const scrollPosition = scrollTop + clientHeight;
    const targetThresholdDown = scrollHeight - scrollHeight * this.scrollThreshold;
    if (scrollPosition >= targetThresholdDown) this.delegate?.scrollThresholdReached();
  }

  private checkRowProximityToEnd(targetRow: number): void {
    if (this.scrollThreshold <= 0) return;
    if (this.itemCount <= 0) return;
    const bufferRows = Math.max(1, Math.ceil(this.scrollThreshold * this.itemCount));
    if (this.itemCount - 1 - targetRow <= bufferRows) this.delegate?.scrollThresholdReached();
  }

  private updateStartNode(scrollTop: number) {
    const low = this.calculateStartNode(scrollTop);

    this.startNode = Math.max(0, low - this.paddingNodes);

    this.startIndexChanged.emit(low); // Emit the start index change event
  }

  /**
   * Calculate offsetY based on cumulative height up to startNode
   */
  private updateVerticalOffset() {
    this.offsetY = this.startNode > 0 ? this.cumulativeHeights[this.startNode - 1] : 0;
  }

  private updateVisibleNodes() {
    this.visibleNodesCount = Math.max(0, this.calculateVisibleNodes());

    this.visibleItems = new Array(this.visibleNodesCount).fill(0).map((_, i) => i + this.startNode);

    // Mark for change detection
    this.changeDetector.markForCheck();

    // Measure row heights and update if necessary
    this.measureRowHeights();
  }

  /* MEASUREMENTS */

  /**
   * Measure the row heights dynamically and update the cumulative heights array.
   */
  private measureRowHeights() {
    let earliestChanged = -1;

    for (const rowElement of this.rowElements ?? []) {
      const height = rowElement.nativeElement.offsetHeight;
      const rowIndex = Number(rowElement.nativeElement.id);

      if (this.rowHeights[rowIndex] === height) continue;

      this.rowHeights[rowIndex] = height;

      if (earliestChanged === -1 || rowIndex < earliestChanged) earliestChanged = rowIndex;
    }

    if (earliestChanged === -1) return;

    this.updateCumulativeHeights(earliestChanged);
  }

  private updateCumulativeHeights(fromIndex = 0): void {
    let cumulativeHeight = fromIndex > 0 ? this.cumulativeHeights[fromIndex - 1] : 0;

    for (let index = fromIndex; index < this.rowHeights.length; index++) {
      cumulativeHeight += this.rowHeights[index];
      this.cumulativeHeights[index] = cumulativeHeight;
    }

    this.cumulativeHeights.length = this.rowHeights.length;
    this.totalContentHeight = cumulativeHeight;
  }

  private adjustRowHeightsToItemCount() {
    if (this.rowHeights.length > this.itemCount) {
      this.rowHeights = this.rowHeights.slice(0, this.itemCount);
      this.cumulativeHeights = this.cumulativeHeights.slice(0, this.itemCount);
    } else if (this.rowHeights.length < this.itemCount) {
      const additionalRows = this.itemCount - this.rowHeights.length;

      this.rowHeights = this.rowHeights.concat(new Array(additionalRows).fill(this.rowHeight));

      for (let i = 0; i < additionalRows; i++) {
        const lastHeight = this.cumulativeHeights[this.cumulativeHeights.length - 1];
        this.cumulativeHeights.push(lastHeight + this.rowHeight);
      }
    }

    this.changeDetector.detectChanges();

    this.totalContentHeight = this.cumulativeHeights[this.cumulativeHeights.length - 1];
  }

  private initializeHeights() {
    this.rowHeights = new Array(this.itemCount).fill(this.rowHeight);
    this.updateCumulativeHeights();
  }

  /* MATH */

  private calculateVisibleNodes(): number {
    const clientHeight = this.viewportElement?.nativeElement?.clientHeight ?? window.innerHeight;

    let totalHeight = 0;
    let visibleNodes = Math.min(this.startNode, this.paddingNodes);

    // Calculate how many nodes fit in the viewport
    for (let i = this.startNode; i < this.itemCount; i++) {
      if (Math.floor(totalHeight / clientHeight) > 0) break;

      totalHeight += this.rowHeights[i];
      visibleNodes++;
    }

    visibleNodes += Math.min(this.paddingNodes, this.itemCount - this.startNode - visibleNodes);

    return visibleNodes;
  }

  private calculateStartNode(scrollTop: number): number {
    if (!this.cumulativeHeights.length) return 0;

    let low = 0;
    let high = this.cumulativeHeights.length - 1;

    // Binary search to find the first row visible at scrollTop
    while (low < high) {
      const mid = Math.floor((low + high) / 2);
      if (this.cumulativeHeights[mid] < scrollTop) {
        low = mid + 1;
      } else {
        high = mid;
      }
    }

    return low;
  }
}
